import { createSelector } from 'reselect';
import { StoreState } from 'state/types';
import { getSiteId, getFrom, getRosteredShifts } from 'state/RosteredShifts';
import { usersBySiteIdSelector } from 'state/UsersCollection';
import { getDutyIcon } from 'lib/helpers/dutyIcons';
import { DutyRequirementsByDay, DutyDisplay } from './types';
import { DayCompliance } from './actions';
import { RosteredShift } from 'type';
import moment, { Moment } from 'moment';
import { values } from 'lodash';

const getDutyRequirementsState = (state: StoreState) => 
  state.dutyRequirements;

const getComplianceData = (state: StoreState, siteId: string) =>
  state.dutyRequirements.compliance[siteId];

/**
 * Check if a set of shifts provides continuous coverage for a time period
 * Supports collective coverage where multiple users work together to cover the period
 * Returns the number of simultaneous coverage slots available
 */
const countContinuousCoverage = (
  shifts: RosteredShift[],
  reqStart: Moment,
  reqEnd: Moment
): number => {
  if (shifts.length === 0) {
    return 0;
  }

  // Sort all shifts by start time
  const sortedShifts = shifts
    .slice()
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  // Check if shifts collectively provide continuous coverage
  let currentCoverage = reqStart.clone();
  const coveringUsers = new Set<string>();

  for (const shift of sortedShifts) {
    // If shift starts after our current coverage point, there's a gap
    if (shift.start.isAfter(currentCoverage)) {
      // Gap detected - no continuous coverage
      return 0;
    }

    // This shift contributes to coverage - add user to set
    coveringUsers.add(shift.user_id!);

    // Extend coverage to the end of this shift if it extends beyond current
    if (shift.end.isAfter(currentCoverage)) {
      currentCoverage = shift.end.clone();
    }
  }

  // Check if we covered the entire period
  if (currentCoverage.isSameOrAfter(reqEnd)) {
    // Return number of unique users involved in providing coverage
    return coveringUsers.size;
  }

  // Didn't reach the end - no continuous coverage
  return 0;
};

/**
 * Transform backend DayCompliance[] to DutyRequirementsByDay[]
 * This converts the backend format to the UI display format
 */
const transformComplianceToDisplay = (
  compliance: DayCompliance[],
  startDate: Moment | string
): DutyRequirementsByDay[] => {
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = moment.parseZone(startDate).add(i, 'days');
    return {
      date: day.format('YYYY-MM-DD'),
      dayOfWeek: day.format('dddd').toLowerCase(),
    };
  });

  return weekDays.map(({ date, dayOfWeek }) => {
    const dayCompliance = compliance.find(c => c.date === date);
    
    if (!dayCompliance || !dayCompliance.duties) {
      return {
        day: dayOfWeek,
        duties: [],
      };
    }

    const duties: DutyDisplay[] = dayCompliance.duties.map((duty) => {
      const timeRanges = duty.periods.map(p => p.timeRange);
      
      return {
        dutyId: duty.dutyId,
        dutyName: duty.dutyName,
        icon: getDutyIcon(duty.dutyName, 16),
        timeRanges,
        totalPersonnel: duty.totalRequired,
        currentStaff: duty.totalCurrent,
        isMet: duty.isMet,
        periodDetails: duty.periods.map(p => ({
          timeRange: p.timeRange,
          required: p.required,
          current: p.current,
          isMet: p.isMet,
        })),
      };
    });

    return {
      day: dayOfWeek,
      duties,
    };
  });
};

export const getCurrentSiteRequirements = createSelector(
  [getDutyRequirementsState, getSiteId],
  (requirementsState, siteId) => requirementsState.data[siteId] || []
);

// Map requirements to days for the current week with actual staff counts
// HYBRID SELECTOR: Prioritizes backend-calculated data (from WebSocket) if available,
// otherwise falls back to frontend calculation
export const getDutyRequirementsByWeekDay = createSelector(
  [
    getSiteId,
    getDutyRequirementsState,
    getCurrentSiteRequirements,
    getFrom,
    getRosteredShifts,
    usersBySiteIdSelector,
  ],
  (
    siteId,
    requirementsState,
    requirements,
    startDate,
    rosteredShifts,
    usersBySite
  ): DutyRequirementsByDay[] => {
    // Check if we have backend-calculated compliance data (from WebSocket)
    const complianceData = requirementsState.compliance[siteId];
    
    if (complianceData && complianceData.length > 0) {
      // Use backend-calculated data (real-time from WebSocket)
      return transformComplianceToDisplay(complianceData, startDate);
    }

    // Fall back to frontend calculation (initial load or when WebSocket unavailable)
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const day = moment.parseZone(startDate).add(i, 'days');
      return {
        date: day.format('YYYY-MM-DD'),
        dayOfWeek: day.format('dddd').toLowerCase(),
      };
    });

    // Convert shifts object to array
    const shiftsArray: RosteredShift[] = values(rosteredShifts);

    // Get users for the current site
    const siteUsers = (usersBySite[siteId] as any) || [];

    return weekDays.map(({ date, dayOfWeek }) => {
      const dayRequirements = requirements.filter(
        (req) => req.day === dayOfWeek
      );

      // Get shifts for this day
      const dayShifts = shiftsArray.filter((shift) => {
        const shiftDate = shift.start.format('YYYY-MM-DD');
        return shiftDate === date && shift.user_id; // Only count assigned shifts
      });

      const duties: DutyDisplay[] = dayRequirements.map((req) => {
        const timeRanges = req.requirements
          ? req.requirements.map(
              (r) => `${r.startTime}-${r.endTime}`
            )
          : [];
        
        const totalPersonnel = req.requirements
          ? req.requirements.reduce((sum, r) => sum + r.minPersonnel, 0)
          : 0;

        // Check each requirement time period independently
        const periodResults = req.requirements?.map((requirement) => {
          // Parse requirement times
          const reqStartTime = moment.parseZone(date)
            .hour(parseInt(requirement.startTime.split(':')[0], 10))
            .minute(parseInt(requirement.startTime.split(':')[1], 10))
            .second(0);
          
          let reqEndTime = moment.parseZone(date)
            .hour(parseInt(requirement.endTime.split(':')[0], 10))
            .minute(parseInt(requirement.endTime.split(':')[1], 10))
            .second(0);

          // Handle overnight periods (e.g., 22:00 - 06:00)
          if (reqEndTime.isSameOrBefore(reqStartTime)) {
            reqEndTime.add(1, 'day');
          }

          // Get all qualified staff shifts that overlap with this requirement
          const qualifiedShifts = dayShifts.filter((shift) => {
            const user = siteUsers.find((u: any) => u.id === shift.user_id);
            const hasDuty = user?.duties?.some((duty: any) => duty.id === req.duty.id);
            
            if (!hasDuty) {
              return false;
            }

            // Check if shift overlaps with this requirement period
            return shift.start.isBefore(reqEndTime) && shift.end.isAfter(reqStartTime);
          });

          // Check if we have continuous coverage for this time period
          const coverageCount = countContinuousCoverage(
            qualifiedShifts,
            reqStartTime,
            reqEndTime
          );

          return {
            minPersonnel: requirement.minPersonnel,
            coverageCount,
            isMet: coverageCount >= requirement.minPersonnel
          };
        }) || [];

        // Count how many periods are met
        const periodsMet = periodResults.filter(p => p.isMet).length;
        const totalPeriods = periodResults.length;
        
        // Overall requirement is met if ALL periods are met
        const isMet = periodsMet === totalPeriods && totalPeriods > 0;
        
        // For display, sum up personnel across all periods
        const currentStaff = periodResults.reduce((sum, p) => sum + p.coverageCount, 0);

        // Build period details for tooltip
        const periodDetails = req.requirements?.map((requirement, index) => {
          // Format time as HH:mm (remove seconds if present)
          const formatTime = (time: string) => {
            const parts = time.split(':');
            return `${parts[0]}:${parts[1]}`;
          };
          
          return {
            timeRange: `${formatTime(requirement.startTime)}-${formatTime(requirement.endTime)}`,
            required: requirement.minPersonnel,
            current: periodResults[index]?.coverageCount || 0,
            isMet: periodResults[index]?.isMet || false,
          };
        }) || [];

        return {
          dutyId: req.duty.id,
          dutyName: req.duty.name,
          icon: getDutyIcon(req.duty.name, 16),
          timeRanges,
          totalPersonnel,
          currentStaff,
          isMet,
          periodDetails,
        };
      });

      return {
        day: dayOfWeek,
        duties,
      };
    });
  }
);